import React, { ReactElement, ReactNode, useMemo } from 'react';
import { AvTooltip, ButtonTypes, FieldListObject, Filter, FiltersListToolbar, Select } from '@fabric-ui/components';
import { Filter as FilterIcon, Plus } from '@fabric-ui/icons';
import { FilterActive } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { emptyArray, emptyObject } from '@fabric-ui/utils';
import { Box, Button, Divider, useTheme } from '@mui/material';
import { extractMeasurementDescription } from './AvFilters.utils';
import { FilterDndContext } from './AvFiltersDnd/FilterDndContext';
import { SortableFilter } from './AvFiltersDnd/SortableFilter';
import { useFilterConfig } from './filters/FilterContext';
import FiltersEmptyState from './FiltersEmptyState';
import { useAvFiltersState } from './useAvFiltersState';
import WithDescriptionTooltip from './WithDescriptionTooltip/WithDescriptionTooltip';

const filterIcon = <FilterIcon style={{ width: 20, height: 20, color: 'var(--color-content-base-tertiary)' }} />;
const filterActiveIcon = <FilterActive style={{ width: 20, height: 20 }} />;
const filtersDropdownWidth = '360px';

const ConditionalTooltip: React.FC<{ title?: ReactNode; enabled: boolean; children: ReactElement }> = ({ title, enabled, children }) =>
  enabled ? <AvTooltip title={title}>{children}</AvTooltip> : children;

export interface AvFiltersProps {
  currentTypeLabels?: { [key: string]: string };
  filters: { headCells?: string[]; [key: string]: any };
  staticFilters?: string[];
  updateFilters: (field: string, val: any) => void;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  onApplyFilter?: () => void;
  size?: 'xSmall' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  isActiveFilter?: boolean;
  onClearFilters?: () => void;
  dims?: Partial<FieldListObject>[];
  metrics?: any[];
  calcDims?: any[];
  usage?: 'SPF' | undefined;
  overrideExtra?: string[];
  activeProjName?: string;
  moreFiltersLabel?: React.ReactNode;
  displayNameMapFormatter?: { (key: string): (val: any) => string };
  children?: ReactElement<any, any>;
  queryKey?: string;
  isFilterExpression?: boolean;
  isGlobalFilter?: boolean;
  showIcon?: boolean;
  isDraggable?: boolean;
  removeFieldButton?: boolean;
  ignoreVisibilityConfig?: boolean;
  displayRecommendedFields?: boolean;
  enableQueryObj?: boolean;
  extraOperands?: Filter[];
  isFilterForIngressValues?: boolean;
  fieldsListAdornment?: React.ReactNode;
  showAdvanced?: boolean;
  /** Gray out selected filter values that the cascaded options query no longer returns. */
  indicateExcludedValues?: boolean;
  /**
   * Narrow each field's option list by these filters, minus the field's own. Opt-in, and only takes effect
   * together with `enableQueryObj` - the SQL transport can't resolve cross-projection fields.
   */
  cascadeFilters?: Filter;
  disableAddFilter?: boolean;
  addFilterTooltip?: ReactNode;
  inactiveFields?: ReadonlySet<string>;
  renderInactiveFieldAdornment?: (field: string) => ReactNode;
}

export const AvFiltersBase: React.FC<AvFiltersProps> = ({
  filters,
  staticFilters = emptyArray,
  updateFilters,
  setFilters,
  onApplyFilter,
  isActiveFilter,
  onClearFilters,
  dims = emptyArray,
  metrics = emptyArray,
  calcDims = emptyArray,
  usage,
  overrideExtra,
  displayNameMapFormatter = emptyObject,
  isFilterExpression = false,
  moreFiltersLabel = (
    <>
      <Plus /> More
    </>
  ),
  isDraggable,
  removeFieldButton,
  children,
  queryKey,
  isGlobalFilter = false,
  showIcon = false,
  ignoreVisibilityConfig = false,
  extraOperands,
  fieldsListAdornment,
  cascadeFilters,
  disableAddFilter,
  addFilterTooltip,
  inactiveFields,
  renderInactiveFieldAdornment,
}) => {
  const theme = useTheme();
  const { size, disabled } = useFilterConfig();
  const {
    visibleDims,
    allSelectedFilters,
    selectedFiltersByFilterType,
    activeFilter,
    fields,
    filtersProps,
    initFilters,
    resetFilters,
    resetFilterType,
    onSelectedFiltersSelect,
    handleReorder,
    renderFilterChip,
    measurements,
    isRecommendedFieldsEnabled,
    isTopFields,
  } = useAvFiltersState({
    filters,
    staticFilters,
    updateFilters,
    setFilters,
    isActiveFilter,
    dims,
    metrics,
    calcDims,
    usage,
    overrideExtra,
    displayNameMapFormatter,
    isFilterExpression,
    removeFieldButton,
    queryKey,
    isGlobalFilter,
    ignoreVisibilityConfig,
    extraOperands,
    cascadeFilters,
    inactiveFields,
    renderInactiveFieldAdornment,
  });

  const availableFields = useMemo(
    () => (inactiveFields ? fields.filter(({ value }) => !inactiveFields.has(value)) : fields),
    [fields, inactiveFields]
  );
  const rounded = (theme.components?.MuiPaper?.styleOverrides?.rounded as any) || {};

  return (
    <Box
      sx={{
        ...flex.itemsCenter,
        flexWrap: 'wrap',
        gap: 1,
        '> svg': {
          mr: 1,
          opacity: disabled ? 0.4 : 1,
        },
      }}>
      {(showIcon || !!Object.keys(filters).length) && (activeFilter ? filterActiveIcon : filterIcon)}
      {children}
      {!!allSelectedFilters.length && (
        <FilterDndContext
          order={allSelectedFilters}
          isDraggable={!!isDraggable}
          disabled={!!disabled}
          onReorder={handleReorder}
          renderItem={renderFilterChip}>
          {allSelectedFilters.map(field => (
            <SortableFilter
              key={field}
              id={field}
              disabled={!isDraggable || !!disabled || !!inactiveFields?.has(field) || allSelectedFilters.length === 1}
              showHandle={!!isDraggable && !disabled && !inactiveFields?.has(field)}
              rounded={rounded}>
              {renderFilterChip(field)}
            </SortableFilter>
          ))}
        </FilterDndContext>
      )}
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        {!disabled && (visibleDims.length > 0 || metrics.length > 0 || disableAddFilter) && (
          <ConditionalTooltip title={addFilterTooltip} enabled={!!disableAddFilter}>
            <span>
              <Select
                type="button"
                variant="text"
                transparent
                showSelection={false}
                size={size}
                disabled={disableAddFilter}
                label={
                  <Box sx={{ ...flex.itemsCenter, gap: 1, fontWeight: 600 }}>
                    {allSelectedFilters.length ? (
                      moreFiltersLabel
                    ) : (
                      <>
                        <Plus />
                        Add Filters
                      </>
                    )}
                  </Box>
                }
                onChange={onSelectedFiltersSelect}
                value={selectedFiltersByFilterType}
                isMultiple
                totalOptionsCount={availableFields.length}
                filterFunc={(options: any, { inputValue }) =>
                  options
                    .filter(
                      o =>
                        o.title?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        o.group?.toLowerCase().includes(inputValue.toLowerCase())
                    )
                    .slice(0, 400)
                }
                groupByFunc={(({ group }) => group) as any}
                renderOption={(option: any) => (
                  <WithDescriptionTooltip
                    placement="right"
                    title={extractMeasurementDescription(option, measurements.asObject)}
                    badgify
                    badgeProps={{ sx: { display: 'list-item' } }}>
                    {option.title}
                  </WithDescriptionTooltip>
                )}
                options={availableFields}
                inputToolbar={
                  <>
                    <FiltersListToolbar
                      {...filtersProps}
                      hiddenButtons={
                        new Set([ButtonTypes.All, ...(isRecommendedFieldsEnabled ? [] : [ButtonTypes.TopFields, ButtonTypes.Populated])])
                      }
                      maxWidth={filtersDropdownWidth}
                    />
                    {fieldsListAdornment}
                  </>
                }
                onClose={isTopFields ? initFilters : resetFilterType}
                filterPopperMinWidth={filtersDropdownWidth}
                emptyState={
                  <FiltersEmptyState
                    onClick={resetFilters}
                    activeFilterType={filtersProps.activeFilterType}
                    isTopFieldsFilterActive={filtersProps.topFields.isTopFieldsFilterActive}
                    isInUseFilterActive={filtersProps.inUse.isInUseFilterActive}
                    isUnpopulatedFieldsActive={filtersProps.unpopulated.isUnpopulatedFieldsActive}
                  />
                }
              />
            </span>
          </ConditionalTooltip>
        )}
        {onClearFilters && !!allSelectedFilters.length && (
          <>
            <Divider orientation="vertical" sx={{ height: 16, alignSelf: 'center' }} flexItem />
            <Button variant="link" disabled={disabled} size={size} onClick={() => onClearFilters()}>
              Clear Filters
            </Button>
          </>
        )}
        {onApplyFilter && !!allSelectedFilters.length && (
          <Button variant="contained" size="xSmall" onClick={() => onApplyFilter()}>
            Apply
          </Button>
        )}
      </Box>
    </Box>
  );
};
